import { iconArrowRight } from "assets/icons";
import { Button } from "components/Button";
import { FilterProps } from "components/TableModified/TableModifiedFilters";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

interface Property {
  name?: string;
  value?: string;
  action?: () => void;
}

type ExhibitorFile = {
  fileId: string;
  filename: string;
  fileUrl: string;
  signedFileUrl: string;
  mimeType: string;
};

export interface Exhibitor {
  exhibitorId: string;
  eventId: string;
  name: string;
  description: string;
  logo: {
    fileId: string;
    filename: string;
    fileUrl: string;
    signedFileUrl: string;
    mimeType: string;
  } | null;
  websiteUrl: string;
  boothNumber: string;
  sponsorTier: string;
  isSponsor: boolean;
  contactEmail: string;
  contactPhone: string;
  displayPosition?: number;
  insertDate: string;
  updateDate: string;
  files: ExhibitorFile[];
}

interface ExhibitorRowProps {
  name: string;
  sponsorTier: string;
  boothNumber: string;
  isSponsor: string;
  contactEmail: string;
  displayPosition: number | string;
  actions: JSX.Element[];
}

export const sponsorTierMap: Record<string, string> = {
  Diamond: "Diamante",
  Platinum: "Platina",
  Gold: "Ouro",
  Silver: "Prata",
  Bronze: "Bronze",
  Support: "Apoio",
};

export const useSponsorsTable = () => {
  const { id: eventId } = useParams<{ id: string }>();
  const initialTableFilters: FilterProps = {
    sponsorTier: {
      label: "Nível de Patrocínio",
      value: "",
      options: [
        { value: "Diamond", label: "Diamante" },
        { value: "Platinum", label: "Platina" },
        { value: "Gold", label: "Ouro" },
        { value: "Silver", label: "Prata" },
        { value: "Bronze", label: "Bronze" },
        { value: "Support", label: "Apoio" },
      ],
    },
    isSponsor: {
      label: "É Patrocinador",
      value: "",
      options: [
        { value: "true", label: "Sim" },
        { value: "false", label: "Não" },
      ],
    },
  };

  const [properties, setProperties] = useState<Property[]>();
  const [exhibitors, setExhibitors] = useState<Exhibitor[]>();
  const [exhibitorRows, setExhibitorRows] = useState<ExhibitorRowProps[]>([]);
  const [tableFilters, setTableFilters] =
    useState<FilterProps>(initialTableFilters);
  const [searchText, setSearchText] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [exhibitorsPerPage, setExhibitorsPerPage] = useState<number>(10);
  const [exhibitorsCount, setExhibitorsCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const { push } = useHistory();

  const rowActionButton = (route: string) => {
    return (
      <Button
        label=""
        size="small"
        icon={iconArrowRight}
        onlyIcon={true}
        onClick={() => push(route)}
      />
    );
  };

  const searchTextFormRef = useRef<any>(null);
  const { connectGraphQLApi, cancelRequest } = useGraphQLAuth();
  const { setToast } = useToast();

  /* HOOKS */
  useEffect(() => {
    return () => {
      cancelRequest();
    };
  }, []);

  useEffect(() => {
    setPageNumber(1);
  }, [tableFilters, searchText, exhibitorsPerPage]);

  useEffect(() => {
    getExhibitors();
  }, [tableFilters, searchText, pageNumber, exhibitorsPerPage]);

  /* EVENT HANDLERS */
  const handleSearchTextFormSubmit = (event: any) => {
    event.preventDefault();
    const text = event.target.searchText.value;
    setSearchText(text);
  };

  const handleFilterChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const eventName = event.target.name;
    const eventValue = event.target.value;
    setTableFilters({
      ...tableFilters,
      [eventName]: {
        ...tableFilters[eventName],
        value: eventValue,
      },
    });
  };

  const handleFilterRemove = (filterName: string): void => {
    setTableFilters({
      ...tableFilters,
      [filterName]: {
        ...tableFilters[filterName],
        value: "",
      },
    });
  };

  const handleSearchTextRemove = (): void => {
    searchTextFormRef.current.reset();
    setSearchText("");
  };

  const handlePageSelected = (page: number): void => {
    setPageNumber(page);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const eventValue = event.target.value;
    setExhibitorsPerPage(parseInt(eventValue));
  };

  /* API CALLS */
  const getExhibitors = async () => {
    setLoading(true);
    cancelRequest();

    const query = `
      query BackofficeExhibitors($filter: ExhibitorFilterInput, $pagination: PaginationInput) {
        backofficeExhibitors(filter: $filter, pagination: $pagination) {
          exhibitorId
          eventId
          name
          description
          logo {
            fileId
            filename
            fileUrl
            signedFileUrl
            mimeType
          }
          websiteUrl
          boothNumber
          sponsorTier
          isSponsor
          contactEmail
          contactPhone
          displayPosition
          insertDate
          updateDate
          files {
            fileId
            filename
            fileUrl
            signedFileUrl
            mimeType
          }

        }
      }
    `;

    const filter = {
      eventId,
      sponsorTier: tableFilters.sponsorTier?.value || undefined,
      isSponsor: tableFilters.isSponsor?.value
        ? tableFilters.isSponsor.value === "true"
        : undefined,
      searchText: searchText || undefined,
    };

    const variables = {
      filter,
      // pagination: {
      //   offset: (pageNumber - 1) * exhibitorsPerPage,
      //   limit: exhibitorsPerPage,
      // }
    };

    try {
      const response = await connectGraphQLApi({
        query,
        variables,
        errorMessage: "Falha ao listar expositores",
        useNewApi: true,
      });

      setError(false);
      const responseData = (response as any).data;
      const exhibitors: Exhibitor[] = responseData.backofficeExhibitors || [];

      // Count totals for properties
      const totalCount = exhibitors.length; // This is a simplified count, ideally we'd have a separate count query
      const sponsorsCount = exhibitors.filter((e) => e.isSponsor).length;
      const exhibitorsOnlyCount = exhibitors.filter((e) => !e.isSponsor).length;

      // Create properties based on the exhibitors data
      const properties: Property[] = [
        { name: "Total de Expositores", value: totalCount.toString() },
        { name: "Patrocinadores", value: sponsorsCount.toString() },
        { name: "Expositores", value: exhibitorsOnlyCount.toString() },
      ];

      const exhibitorRows = exhibitors.map((exhibitor: Exhibitor) => {
        const actions: JSX.Element[] = [
          rowActionButton(
            `/events/${eventId}/exhibitor/${exhibitor.exhibitorId}/edit`
          ),
        ];

        return {
          name: exhibitor.name || "Não informado",
          sponsorTier: exhibitor.sponsorTier
            ? sponsorTierMap[exhibitor.sponsorTier] || exhibitor.sponsorTier
            : "Não informado",
          boothNumber: exhibitor.boothNumber || "Não informado",
          isSponsor: exhibitor.isSponsor ? "Sim" : "Não",
          contactEmail: exhibitor.contactEmail || "Não informado",
          displayPosition: exhibitor.displayPosition || "Não informado",
          actions,
        };
      });

      setProperties(properties);
      setExhibitors(exhibitors);
      setExhibitorsCount(totalCount); // In a real scenario, we'd get this from a count query
      setExhibitorRows(exhibitorRows);
      setLoading(false);
    } catch (error: any) {
      console.error("Error details:", error);
      if (error?.errorType === "connection") {
        setError(true);
        setLoading(false);
      }
    }
  };

  return {
    loading,
    error,
    properties,
    exhibitorRows,
    exhibitorsCount,
    searchText,
    tableFilters,
    searchTextFormRef,
    pageNumber,
    exhibitorsPerPage,
    handleSearchTextFormSubmit,
    handleFilterChange,
    handleFilterRemove,
    handleSearchTextRemove,
    handlePageSelected,
    handleRowsPerPageChange,
    exhibitors,
    eventId,
  };
};
